// Quittances et reçus (F-052, SRC-12, D-16) : établies à partir des paiements confirmés ; envoi électronique
// uniquement avec l'accord du locataire ; un document émis n'est jamais supprimé.
import { useState } from "react";
import { post } from "@core/api";
import { useGet } from "@core/hooks";
import { date, dateTime } from "@core/format";
import { Badge, Button, Card, Empty, ErrorBox, Loading, Notice, PageHeader, Table } from "@core/ui";
import { Amount, DocLink, MonthPicker, RECEIPT_STATUS, currentMonth, useAction, useG } from "./shared";

type Receipt = {
  id: string;
  lease_id: string;
  rent_call_id: string;
  kind: string;
  number: string;
  period_start: string;
  period_end: string;
  amount_cents: number;
  due_cents: number;
  status: string;
  document_id: string | null;
  sent_at: string | null;
  sent_to: string;
  cancelled_reason: string;
  property_label: string;
  tenants: string | null;
  e_receipts_consent: boolean;
};
type ToIssue = { rent_call_id: string; lease_id: string; period_start: string; period_end: string; total_cents: number; property_label: string; tenants: string | null };

export function QuittancesPage() {
  const { g, can } = useG();
  const [month, setMonth] = useState(currentMonth());
  const q = useGet<{ items: Receipt[]; to_issue: ToIssue[] }>(g(`/receipts?month=${month}`));
  const { busy, run } = useAction();
  const canIssue = can("finance") || can("operate");
  return (
    <div className="page">
      <PageHeader
        eyebrow="Gestion"
        title="Quittances et reçus"
        intro="La quittance suppose le règlement intégral confirmé de la période ; un paiement partiel donne lieu à un reçu. L’envoi électronique suppose l’accord du locataire ; il peut toujours télécharger ses documents dans son espace."
        actions={<MonthPicker value={month} onChange={setMonth} />}
      />
      {q.isLoading ? <Loading /> : null}
      <ErrorBox error={q.error} />
      {q.data ? (
        <>
          <Card
            title="Quittances à établir"
            actions={
              canIssue ? (
                <Button small busy={busy === "month"} onClick={() => run("month", () => post<any>(g("/receipts/generate"), { month }), (r: any) => `${r.issued.length} quittance(s) établie(s) pour le mois.`)}>
                  Établir les quittances du mois
                </Button>
              ) : null
            }
          >
            {q.data.to_issue.length === 0 ? (
              <p className="muted">Toutes les échéances réglées ont leur quittance.</p>
            ) : (
              <ul className="gestion-list">
                {q.data.to_issue.map((t) => (
                  <li key={t.rent_call_id} className="gestion-line">
                    <span>
                      {t.property_label} · {date(t.period_start)} – {date(t.period_end)} · <Amount cents={t.total_cents} />
                      <span className="block small muted">{t.tenants}</span>
                    </span>
                    {canIssue ? (
                      <Button variant="secondaire" small busy={busy === t.rent_call_id} onClick={() => run(t.rent_call_id, () => post<any>(g(`/rent-calls/${t.rent_call_id}/receipt`)), (r: any) => `Quittance n° ${r.number} établie.`)}>
                        Établir la quittance
                      </Button>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </Card>
          <Card title="Documents établis">
            {q.data.items.length === 0 ? (
              <Empty title="Aucun document pour ce mois" />
            ) : (
              <Table caption="Quittances et reçus">
                <thead>
                  <tr>
                    <th>Document</th>
                    <th>Logement</th>
                    <th className="right">Montant</th>
                    <th>État</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {q.data.items.map((r) => (
                    <tr key={r.id}>
                      <td>
                        {r.kind === "quittance" ? <Badge tone="succes">Quittance</Badge> : <Badge tone="attention">Reçu (paiement partiel)</Badge>} <span className="mono">{r.number}</span>
                        <span className="block small muted">
                          {date(r.period_start)} – {date(r.period_end)}
                        </span>
                      </td>
                      <td>
                        {r.property_label}
                        <span className="block small muted">{r.tenants}</span>
                      </td>
                      <td className="right">
                        <Amount cents={r.amount_cents} />
                        {r.kind === "recu" ? <span className="block small muted">sur <Amount cents={r.due_cents} /></span> : null}
                      </td>
                      <td>
                        {RECEIPT_STATUS[r.status] ?? r.status}
                        {r.sent_at ? <span className="block small muted">Envoyé le {dateTime(r.sent_at)}</span> : null}
                        {r.cancelled_reason ? <span className="block small muted">{r.cancelled_reason}</span> : null}
                      </td>
                      <td className="right">
                        <div className="gestion-actions">
                          <DocLink id={r.document_id}>PDF</DocLink>
                          {r.status !== "annulee" && can("operate") ? (
                            r.e_receipts_consent ? (
                              <Button variant="secondaire" small busy={busy === r.id} onClick={() => run(r.id, () => post(g(`/receipts/${r.id}/send`)), "Document envoyé (envoi simulé, visible dans « Envois »).")}>
                                {r.sent_at ? "Renvoyer" : "Envoyer"}
                              </Button>
                            ) : (
                              <span className="small muted">Accord du locataire requis pour l’envoi électronique</span>
                            )
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
            <Notice>
              Un paiement annulé ou réaffecté annule la quittance concernée avec son motif ; le document émis reste conservé et une nouvelle quittance est établie après régularisation.
            </Notice>
          </Card>
        </>
      ) : null}
    </div>
  );
}
